import { useI18nNavigate } from '@/hooks/use-i18n-navigate';
import { createBrowserHistory, History, Location, Update } from 'history';
import { useEffect } from 'react';
import { To, NavigateOptions, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

// 定义回调函数类型
export type BeforeRouterCallback = (from: Location, to: Location) => boolean | Promise<boolean>;
export type AfterRouterCallback = (from: Location, to: Location) => void | Promise<void>;
// 定义导航函数类型
export type NavigateFunction = (to: To, options?: NavigateOptions) => void;

/**
 * 路由监听单例类
 * 封装路由跳转前后的回调功能，支持多组件独立监听
 */
class RouteNavigateManager {
  private static instance: RouteNavigateManager;
  private history: History;
  private beforeRouterCallbacks: Array<{ id: string, callback: BeforeRouterCallback }> = [];
  private afterRouterCallbacks: Array<{ id: string, callback: AfterRouterCallback }> = [];
  private currentLocation: Location;
  private isNavigating: boolean = false;
  private navigateFunction?: NavigateFunction;
  // 存储当前被拦截的导航目标
  private pendingNavigationTo?: Location;

  private constructor() {
    this.history = createBrowserHistory();
    this.currentLocation = this.history.location;
  }

  public static getInstance(): RouteNavigateManager {
    if (!RouteNavigateManager.instance) {
      RouteNavigateManager.instance = new RouteNavigateManager();
    }
    return RouteNavigateManager.instance;
  }

  /**
   * 设置导航函数，用于实际跳转
   * @param navigateFn 导航函数，如 useNavigate 或自定义实现
   */
  public setNavigateFunction(navigateFn: NavigateFunction): void {
    this.navigateFunction = navigateFn;
  }

  /**
   * 更新当前地址并触发后置回调
   * @param location 新的地址
  */
  public updateCurrentLocation(location: Location): void {
    if (!this.isSameLocation(this.currentLocation, location)) {
      // console.log('更新当前地址并触发后置回调', location);
      // 关键调用, 触发后置路由监听
      this.handleAfterNavigation(location);``
    }
    this.currentLocation = location;
  }

  /**
   * 添加路由跳转前的回调函数
   * @param id 回调唯一标识，用于后续移除
   * @param callback 回调函数
   */
  public addBeforeRouter(id: string, callback: BeforeRouterCallback): void {
    // 先移除已存在的相同id回调，避免重复
    this.removeBeforeRouter(id);
    this.beforeRouterCallbacks.push({ id, callback });
  }

  /**
   * 移除路由跳转前的回调函数
   * @param id 回调唯一标识
   */
  public removeBeforeRouter(id: string): void {
    this.beforeRouterCallbacks = this.beforeRouterCallbacks.filter(
      item => item.id !== id
    );
  }

  /**
   * 添加路由跳转后的回调函数
   * @param id 回调唯一标识，用于后续移除
   * @param callback 回调函数
   */
  public addAfterRouter(id: string, callback: AfterRouterCallback): void {
    this.removeAfterRouter(id);
    this.afterRouterCallbacks.push({ id, callback });
  }

  /**
   * 移除路由跳转后的回调函数
   * @param id 回调唯一标识
   */
  public removeAfterRouter(id: string): void {
    this.afterRouterCallbacks = this.afterRouterCallbacks.filter(
      item => item.id !== id
    );
  }

  /** 清空所有前置回调 */
  public clearBeforeRouter(): void {
    this.beforeRouterCallbacks = [];
  }

  /** 清空所有后置回调 */
  public clearAfterRouter(): void {
    this.afterRouterCallbacks = [];
  }

  /**
   * 处理路由跳转后的回调
   * @param to 目标位置
   */
  private async handleAfterNavigation(to: Location): Promise<void> {
    if (!this.isSameLocation(this.currentLocation, to)) {
      // 执行所有后置回调
      console.log('Execute all after router callbacks:', { callbacks: this.afterRouterCallbacks });
      for (const { id, callback } of this.afterRouterCallbacks) {
        try {
          const result = callback(this.currentLocation, to);
          if (result instanceof Promise) {
            await result;
          }
        } catch (err) {
          console.error('afterRouter回调执行出错:', err);
        }
      }
    }

    this.currentLocation = { ...to };
    // 导航完成后清除 pending 状态
    this.pendingNavigationTo = undefined;
  }

  /**
   * 执行路由跳转
   * @param to 目标位置
   * @param options 跳转选项
   * @returns 是否成功跳转
   */
  public async navigate(to: To, options?: NavigateOptions): Promise<boolean> {
    const toLocation = this.createToLocation(to);

    if (this.isSameLocation(this.currentLocation, toLocation)) return;
    if (this.isNavigating) return false;

    try {
      this.isNavigating = true;
      // 保存当前导航目标
      this.pendingNavigationTo = toLocation;

      // 只执行当前页面注册的前置回调
      const currentPageCallbacks = this.beforeRouterCallbacks.filter(() => {
        // 这里假设所有已注册的回调都属于当前页面
        // 实际应用中可能需要根据页面标识进行过滤
        return true;
      });

      // 执行当前页面的前置回调，全部同意才继续
      let shouldProceed = true;
      for (const { callback } of currentPageCallbacks) {
        try {
          const result = callback(this.currentLocation, toLocation);
          const callbackResult = result instanceof Promise ? await result : result;
          if (!callbackResult) {
            shouldProceed = false;
            break; // 有一个回调不同意就终止
          }
        } catch (err) {
          console.error('beforeRouter回调执行出错:', err);
          shouldProceed = false;
          break;
        }
      }

      // 允许跳转
      if (shouldProceed) {
        return this.executeNavigation(toLocation, options);
      }
      return false;
    } finally {
      this.isNavigating = false;
    }
  }

  /**
   * 继续执行挂起的导航
   * @param options 跳转选项
   * @returns 是否成功跳转
   */
  public async proceedPendingNavigation(options?: NavigateOptions): Promise<boolean> {
    if (!this.pendingNavigationTo) return false;

    return this.executeNavigation(this.pendingNavigationTo, options);
  }

  /**
   * 执行路由跳转
   * @param to 目标位置
   * @param options 跳转选项
   * @returns 是否成功跳转
   */
  private async executeNavigation(to: Location, options?: NavigateOptions): Promise<boolean> {
    try {
      this.isNavigating = true;
      if (this.navigateFunction) {
        this.navigateFunction(to.pathname, options);
      } else {
        if (options?.replace) {
          this.history.replace(to, options.state);
        } else {
          this.history.push(to, options?.state);
        }
      }
      this.handleAfterNavigation(to);
      return true;
    } catch (err) {
      console.error('executeNavigation执行出错:', err);
      return false;
    } finally {
      this.isNavigating = false;
    }
  }

  public getHistory(): History {
    return this.history;
  }

  public getLocation(): Location {
    return { ...this.currentLocation };
  }

  /**
   * 创建目标位置对象
   * @param to 目标位置
   * @returns 位置对象
   */
  private createToLocation(to: To): Location {
    if (typeof to === 'string') {
      return {
        ...this.currentLocation,
        pathname: to,
        key: uuid()
      };
    }

    return {
      ...this.currentLocation,
      ...to,
      key: uuid()
    };
  }

  /**
   * 判断两个位置是否相同
   * @param a 位置A
   * @param b 位置B
   * @returns 是否相同
   */
  private isSameLocation(a: Location, b: Location): boolean {
    return (
      a.pathname === b.pathname &&
      a.search === b.search &&
      a.hash === b.hash
    );
  }
}

// ====================== Hook ======================

// 初始化路由导航
export function useInitialRouterI18nNavigate() {
  // 使用外部导航函数
  const i18nNavigate = useI18nNavigate();
  const location = useLocation();

  useEffect(() => {
    routeNavigateManager.setNavigateFunction(i18nNavigate);
    // 初始化时设置一次位置
    // routeNavigateManager.updateCurrentLocation(location);

    return () => {
      // 可以选择在这里重置导航函数
      // routeNavigateManager.setNavigateFunction(undefined);
    };
  }, [i18nNavigate]);

  // 监听位置变化，更新类中的当前位置
  useEffect(() => {
    routeNavigateManager.updateCurrentLocation(location);
  }, [location]);

  return (to: string, options?: NavigateOptions) => {
    routeNavigateManager.navigate(to, options);
  };
}

// 路由导航, 建议在需要监听路由的前提下使用
const useNavigate = () => {
  const navigate = (to: To, options?: NavigateOptions) => {
    routeNavigateManager.navigate(to, options);
  }
  return {
    navigate
  }
}

// 监听路由
const useRouterListener = () => {
  /**
   * 注册前置路由监听
   * @description 前置路由必须使用 useNavigate 的跳转方法
   * @param id 唯一标识，建议使用组件名+随机字符串
   * @param callback 回调函数
   * @returns 包含取消监听和继续导航的函数
   */
  const beforeListener = (id: string, callback: BeforeRouterCallback) => {
    routeNavigateManager.addBeforeRouter(id, callback);

    return {
      removeBeforeListener: () => {
        routeNavigateManager.removeBeforeRouter(id);
      },
      next: (options?: NavigateOptions) => {
        return routeNavigateManager.proceedPendingNavigation(options);
      }
    };
  }

  /**
   * 注册后置路由监听
   * @param id 唯一标识
   * @param callback 回调函数
   * @returns 取消监听的函数
   */
  const afterListener = (id: string, callback: AfterRouterCallback) => {
    routeNavigateManager.addAfterRouter(id, callback);

    return {
      removeAfterListener: () => {
        // 异步执行, 后置路由监听需要在导航完成后执行(先调用后置回调,再移除监听)
        setTimeout(() => {
          routeNavigateManager.removeAfterRouter(id);
        }, 0)
      }
    };
  }

  return {
    beforeListener,
    afterListener
  }
}

export const useRouteNavigateHook = {
  useInitialRouterI18nNavigate,
  useNavigate,
  useRouterListener,
}

export const routeNavigateManager = RouteNavigateManager.getInstance();
export default RouteNavigateManager;
